import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import useAxios from "axios-hooks";
import { Loader } from "semantic-ui-react";
import { Button, ContentContainer, Text } from "components/shared";

// Licences: one monthly subscription per wind turbine, paid by card on
// Stripe's hosted page (Orbital-server routes/licenses.js, 2026-10-09).
// An expired or missing licence is only shown as a warning here - nothing
// else in italscada is blocked.

const Card = styled.div`
  background-color: ${(props) => props.theme.controlsBackgroundColor};
  border-radius: 12px;
  padding: 1rem 1.25rem 1.25rem;
  margin: 1rem 0;
`;

const Title = styled((props) => <Text {...props} />)`
  display: block;
  color: ${(props) => props.theme.textDark};
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(10rem, 2fr) minmax(10rem, 2fr) auto;
  gap: 1rem;
  align-items: center;
  padding: 0.6rem 0;
  border-top: 1px solid ${(props) => props.theme.textMediumLight};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`;

const Name = styled.span`
  color: ${(props) => props.theme.textDark};
  font-weight: 700;
`;

const Status = styled.span`
  color: ${(props) => props.color};
  font-weight: 600;
`;

const Notice = styled.div`
  color: ${(props) => props.color || props.theme.textMedium};
  margin-bottom: 0.75rem;
`;

const STATUS_COLORS = { ok: "#21ba45", warn: "#f2711c", none: "#767676" };

const describe = (t) => {
  const until = t.currentPeriodEnd ? moment(t.currentPeriodEnd).format("DD/MM/YYYY") : null;
  if (t.active) {
    return {
      color: t.status === "past_due" ? STATUS_COLORS.warn : STATUS_COLORS.ok,
      text:
        t.status === "past_due"
          ? `Payment failed - active until ${until}, please update the card`
          : t.cancelAtPeriodEnd
          ? `Active until ${until} (will not renew)`
          : `Active - renews on ${until}`,
    };
  }
  if (until) return { color: STATUS_COLORS.warn, text: `Expired on ${until}` };
  return { color: STATUS_COLORS.none, text: "No licence" };
};

const query = () => {
  const hash = window.location.hash || "";
  const i = hash.indexOf("?");
  return new URLSearchParams(i >= 0 ? hash.slice(i + 1) : "");
};

const Licenses = () => {
  const [{ data, loading, error }] = useAxios({ url: "/licenses" }, { useCache: false });
  const [, post] = useAxios({ method: "POST" }, { manual: true });
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const params = query();

  const go = async (turbineId, action) => {
    setBusyId(turbineId);
    setActionError(null);
    try {
      const res = await post({ url: `/licenses/${turbineId}/${action}` });
      window.location.href = res.data.url; // Stripe-hosted page
    } catch (e) {
      const msg = e && e.response && e.response.data && (e.response.data.error || e.response.data.message);
      setActionError(msg || "Could not open the payment page - please try again.");
      setBusyId(null);
    }
  };

  return (
    <ContentContainer>
      <Card>
        <Title size={22}>Licences</Title>
        <Notice>
          One monthly licence per wind turbine, paid by card and renewed automatically every month. Payment, invoices and card changes are
          handled on Stripe&apos;s secure page - card details never reach italscada.
        </Notice>
        {params.get("paid") && (
          <Notice color={STATUS_COLORS.ok}>
            Thank you - the payment was sent. The licence shows as active as soon as Stripe confirms
            it (usually within a few seconds; refresh the page).
          </Notice>
        )}
        {params.get("cancelled") && <Notice>Payment cancelled - nothing was charged.</Notice>}
        {data && !data.paymentsEnabled && (
          <Notice color={STATUS_COLORS.warn}>Card payments are not configured yet.</Notice>
        )}
        {actionError && <Notice color="#db2828">{actionError}</Notice>}
        {loading && <Loader active inline="centered" />}
        {error && <Notice color="#db2828">Could not load the licences.</Notice>}
        {data &&
          data.turbines.map((t) => {
            const { color, text } = describe(t);
            return (
              <Row key={t.id}>
                <Name>
                  {t.name} <Text size={12}>({t.identifier})</Text>
                </Name>
                <Status color={color}>{text}</Status>
                <div>
                  {t.canManage && (
                    <Button secondary onClick={() => go(t.id, "portal")} loading={busyId === t.id} disabled={!!busyId}>
                      Manage
                    </Button>
                  )}
                  {!t.active && (
                    <Button
                      onClick={() => go(t.id, "checkout")}
                      loading={busyId === t.id}
                      disabled={!!busyId || !data.paymentsEnabled}
                    >
                      Pay with card
                    </Button>
                  )}
                </div>
              </Row>
            );
          })}
        {data && data.turbines.length === 0 && <Notice>No wind turbines on this account.</Notice>}
      </Card>
    </ContentContainer>
  );
};

export default Licenses;
